import {
  canSelfServePayAsYouGoUpgrade,
  isPayAsYouGoPlanCode,
  payAsYouGoPlan,
  planCodeBase,
  resolveSubscriptionPlanCode,
} from './subscription-plan-code';
import {
  setupCardDialogClassName,
  SetupCard,
} from '@/components/layout/setup-card';
import { Alert, AlertDescription, AlertTitle } from '@/components/v1/ui/alert';
import { Button } from '@/components/v1/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/v1/ui/dialog';
import { Spinner } from '@/components/v1/ui/loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/v1/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/v1/ui/tooltip';
import useControlPlane from '@/hooks/use-control-plane';
import { useOrganizationEntitlements } from '@/hooks/use-organization-entitlements';
import { useOrganizations } from '@/hooks/use-organizations';
import {
  getPlanChangeErrorMessage,
  useSubscriptionUpgrade,
} from '@/hooks/use-subscription-upgrade';
import { useTenantDetails } from '@/hooks/use-tenant';
import { queries } from '@/lib/api';
import {
  OrganizationBillingState,
  OrganizationResourceLimit,
  SubscriptionPlan,
  SubscriptionPlanFeature,
  SubscriptionPlanList,
} from '@/lib/api/generated/control-plane/data-contracts';
import { OFFICE_HOURS_URL, PRICING_URL } from '@/lib/external-links';
import { cn } from '@/lib/utils';
import { formatRetentionPeriod } from '@/lib/utils/retention';
import { appRoutes } from '@/router';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

export type UpgradeGate =
  'tenants' | 'users' | 'retention' | 'usage' | 'organizations';

export type UpgradeGateProps = {
  gate: UpgradeGate;
  organizationId: string;
  onDismiss?: () => void;
  /**
   * Feature that hit its limit (e.g. `task_runs`, `crons`). Names the resource
   * in the header and highlights its row for the `usage` gate. Omit for a
   * generic "upgrade" entry point with no specific limit.
   */
  featureId?: string;
  retentionPeriod?: string;
  /**
   * Name of the organization the upgrade applies to. Used by the
   * organizations gate so the copy names the current organization.
   */
  organizationName?: string;
};

// ---------------------------------------------------------------------------
// Copy. Keys mirror docs/plans/upgrade-gate-copy.mdx; edit there, then here.
// ---------------------------------------------------------------------------

const COPY = {
  planName: 'Pay as you Go',
  freeName: 'Free',
  header: {
    tenants: {
      title: "You've hit your tenant limit",
      description:
        'Upgrade to pay-as-you-go to add more tenants and separate dev, staging, and prod.',
    },
    organizations: {
      title: 'Upgrade your current organization',
      description: (name?: string) =>
        name
          ? `Upgrade ${name} to pay-as-you-go to create another organization.`
          : 'Upgrade your current organization to pay-as-you-go to create another one.',
    },
    users: {
      title: "You've hit your member limit",
      description: 'Upgrade to pay-as-you-go to bring in your whole team.',
    },
    retention: {
      title: "You've gone past your retention window",
      description: 'Upgrade to pay-as-you-go to unlock longer retention.',
    },
    retentionCustom: {
      title: "You've gone past your retention window",
      description: (plan: string, retention: string) =>
        `Your ${plan} plan keeps ${retention} of data. Contact us for a custom plan with longer retention.`,
      checking: 'Checking your plan.',
    },
    usageResource: {
      title: (resource: string) =>
        `You've hit your limit for ${resource.toLowerCase()}`,
      description: (resource: string) =>
        `Upgrade to pay-as-you-go to unlock more ${resource.toLowerCase()}.`,
    },
    usageGeneric: {
      title: 'Upgrade to Pay as you Go',
      description: 'Upgrade to pay-as-you-go to remove the free tier limits.',
    },
  },
  compare: {
    resource: 'Resource',
    perDay: '/ day',
    perMonth: '/ month',
    unlimited: 'Unlimited',
    dailyCap: 'hard daily cap',
    overage: (price: string, units: string) => `then ${price} per ${units}`,
    groups: {
      metered: 'Metered usage',
      meteredNote: 'Included each month, then billed per use',
      limits: 'Plan limits',
    },
    expand: 'See full breakdown',
    collapse: 'Hide full breakdown',
    currentPlan: 'Current plan',
  },
  actions: {
    upgrade: 'Upgrade',
    ownerOnly: 'Only organization owners can upgrade.',
    billing: 'View billing',
    dismiss: 'Not now',
    contact: 'Contact us',
    footnote:
      'No monthly fee. You pay nothing until you scale past the included usage.',
    pricing: 'Pricing details',
    salesLead: 'Need volume discounts or custom terms?',
    sales: 'Talk to us',
  },
  error: {
    title: "We couldn't change your plan",
  },
} as const;

// Row order in the comparison table, grouped by kind. `label` is user-facing.
// Metered resources have an included monthly amount and then bill per use;
// limits are hard ceilings on the plan.
const RESOURCES = [
  { id: 'task_runs', label: 'Task runs', kind: 'metered' },
  { id: 'events', label: 'Events', kind: 'metered' },
  { id: 'tenants', label: 'Tenants', kind: 'limit' },
  { id: 'users', label: 'Members', kind: 'limit' },
  { id: 'data_retention_days', label: 'Data retention', kind: 'limit' },
  { id: 'worker_slots_limit', label: 'Concurrent runs', kind: 'limit' },
  { id: 'crons', label: 'Crons', kind: 'limit' },
  { id: 'scheduled_runs', label: 'Scheduled runs', kind: 'limit' },
  { id: 'webhooks', label: 'Webhook endpoints', kind: 'limit' },
] as const;

type ResourceId = (typeof RESOURCES)[number]['id'];
type ResourceKind = (typeof RESOURCES)[number]['kind'];

// Only shown when the plans API is unavailable (e.g. billing disabled).
const PAYG_FALLBACK: Record<ResourceId, string> = {
  tenants: '10',
  users: '50',
  data_retention_days: '7',
  task_runs: '1M',
  events: '10M',
  worker_slots_limit: '500',
  crons: '1,000',
  scheduled_runs: '1M',
  webhooks: '100',
};

// Only shown when the plans API is unavailable. Tenants, members, and
// retention prefer the org's live entitlements; task runs and events prefer
// the plan list's daily free limits.
const FREE_FALLBACK: Record<ResourceId, string> = {
  tenants: '1',
  users: '3',
  data_retention_days: '1',
  task_runs: '1,000',
  events: '10,000',
  worker_slots_limit: '100',
  crons: '5',
  scheduled_runs: '100',
  webhooks: '5',
};

// Overage pricing shown when the plans API is unavailable.
const OVERAGE_FALLBACK: Partial<
  Record<ResourceId, { price: number; units: number }>
> = {
  task_runs: { price: 10, units: 1_000_000 },
  events: { price: 2, units: 1_000_000 },
};

const DAILY_LIMIT_TO_RESOURCE: Record<string, ResourceId> = {
  task_runs_daily_limit: 'task_runs',
  events_daily_limit: 'events',
};

type ComparisonRow = {
  id: ResourceId;
  kind: ResourceKind;
  label: string;
  free: string;
  freeNote?: string;
  payg: string;
  paygNote?: string;
};

type Comparison = {
  rows: ComparisonRow[];
  highlightId: ResourceId | null;
};

type GateHeader = {
  title: string;
  description: string;
};

type Entitlements = {
  tenants?: OrganizationResourceLimit;
  users?: OrganizationResourceLimit;
};

// ---------------------------------------------------------------------------
// Data helpers
// ---------------------------------------------------------------------------

function findFeature(plan: SubscriptionPlan | undefined, featureId: string) {
  return plan?.featureGroups
    ?.flatMap((group) => group.features)
    .find((feature) => feature.featureId === featureId);
}

function formatCount(value: number) {
  if (value >= 1_000_000 && value % 1_000_000 === 0) {
    return `${value / 1_000_000}M`;
  }
  return new Intl.NumberFormat('en-US').format(value);
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatOverage(
  feature: SubscriptionPlanFeature | undefined,
  fallback: { price: number; units: number } | undefined,
) {
  const overage = feature?.overage
    ? { price: feature.overage.price, units: feature.overage.billingUnits }
    : fallback;
  if (!overage || overage.units <= 0) {
    return undefined;
  }
  return COPY.compare.overage(
    usd.format(overage.price),
    formatCount(overage.units),
  );
}

function formatIncluded(feature: SubscriptionPlanFeature | undefined) {
  if (!feature || !feature.included) {
    return null;
  }
  if (feature.unlimited) {
    return COPY.compare.unlimited;
  }
  return formatCount(feature.includedUsage);
}

function formatResourceLimit(limit: OrganizationResourceLimit | undefined) {
  if (!limit) {
    return null;
  }
  if (limit.unlimited || limit.limit < 0) {
    return COPY.compare.unlimited;
  }
  return formatCount(limit.limit);
}

function formatDays(value: string) {
  if (value === COPY.compare.unlimited) {
    return value;
  }
  return value === '1' ? '1 day' : `${value} days`;
}

function freePlan(plans?: SubscriptionPlan[]) {
  return plans?.find((plan) => planCodeBase(plan.planCode) === 'free');
}

function toResourceId(featureId?: string): ResourceId | null {
  if (!featureId) {
    return null;
  }
  const mapped = DAILY_LIMIT_TO_RESOURCE[featureId] ?? featureId;
  return RESOURCES.some((resource) => resource.id === mapped)
    ? (mapped as ResourceId)
    : null;
}

function salesUrl(tenantName?: string, tenantId?: string) {
  if (!tenantName && !tenantId) {
    return OFFICE_HOURS_URL;
  }
  return `https://cal.com/team/hatchet/website-demo?notes=${encodeURIComponent(
    `Custom pricing request for tenant '${tenantName || 'Unknown'}' (${tenantId ?? ''})`,
  )}`;
}

function buildComparison(input: {
  planList?: SubscriptionPlanList;
  entitlements?: Entitlements;
  retentionPeriod?: string;
  highlightId: ResourceId | null;
}): Comparison {
  const paygPlan = payAsYouGoPlan(input.planList?.plans);
  const free = freePlan(input.planList?.plans);
  const freeDaily = new Map(
    (input.planList?.freeLimits ?? []).map((limit) => [
      DAILY_LIMIT_TO_RESOURCE[limit.featureId] ?? limit.featureId,
      formatCount(limit.limit),
    ]),
  );

  const paygValue = (id: ResourceId) =>
    formatIncluded(findFeature(paygPlan, id)) ?? PAYG_FALLBACK[id];

  const freeValue = (id: ResourceId) => {
    const fromPlan = formatIncluded(findFeature(free, id));
    switch (id) {
      case 'tenants':
        return (
          formatResourceLimit(input.entitlements?.tenants) ??
          fromPlan ??
          FREE_FALLBACK[id]
        );
      case 'users':
        return (
          formatResourceLimit(input.entitlements?.users) ??
          fromPlan ??
          FREE_FALLBACK[id]
        );
      case 'task_runs':
      case 'events':
        return freeDaily.get(id) ?? fromPlan ?? FREE_FALLBACK[id];
      default:
        return fromPlan ?? FREE_FALLBACK[id];
    }
  };

  const rows = RESOURCES.map((resource): ComparisonRow => {
    const { id, kind, label } = resource;
    let free = freeValue(id);
    let payg = paygValue(id);

    if (id === 'data_retention_days') {
      free = input.retentionPeriod
        ? formatRetentionPeriod(input.retentionPeriod)
        : formatDays(free);
      payg = formatDays(payg);
    }

    if (kind === 'metered') {
      return {
        id,
        kind,
        label,
        free: `${free} ${COPY.compare.perDay}`,
        freeNote: COPY.compare.dailyCap,
        payg: `${payg} ${COPY.compare.perMonth}`,
        paygNote: formatOverage(
          findFeature(paygPlan, id),
          OVERAGE_FALLBACK[id],
        ),
      };
    }

    return { id, kind, label, free, payg };
  });

  return { rows, highlightId: input.highlightId };
}

function buildHeader(
  gate: UpgradeGate,
  row: ComparisonRow | undefined,
  organizationName?: string,
): GateHeader {
  switch (gate) {
    case 'tenants':
      return COPY.header.tenants;
    case 'organizations':
      return {
        title: COPY.header.organizations.title,
        description: COPY.header.organizations.description(organizationName),
      };
    case 'users':
      return COPY.header.users;
    case 'retention':
      return COPY.header.retention;
    case 'usage':
    default:
      if (row) {
        return {
          title: COPY.header.usageResource.title(row.label),
          description: COPY.header.usageResource.description(row.label),
        };
      }
      return COPY.header.usageGeneric;
  }
}

type GateMode = 'upgrade' | 'custom' | 'loading';

function currentPlanCode(state: OrganizationBillingState | undefined) {
  if (!state) {
    return null;
  }
  return resolveSubscriptionPlanCode(state.currentSubscription, null) || 'free';
}

function currentPlanDisplayName(
  plans: SubscriptionPlan[] | undefined,
  planCode: string,
) {
  if (isPayAsYouGoPlanCode(planCode)) {
    return COPY.planName;
  }
  const match =
    plans?.find((plan) => plan.planCode === planCode) ??
    plans?.find(
      (plan) => planCodeBase(plan.planCode) === planCodeBase(planCode),
    );
  return match?.name || planCode;
}

// ---------------------------------------------------------------------------
// Hook: everything the dialog and the inline card need
// ---------------------------------------------------------------------------

function useUpgradeGate({
  gate,
  organizationId,
  featureId,
  retentionPeriod,
  organizationName,
  onUpgraded,
}: Omit<UpgradeGateProps, 'onDismiss'> & {
  // A saved card updates the plan in place. Close the dialog only then;
  // a checkout URL navigates away on its own.
  onUpgraded?: () => void;
}) {
  const { canBill, isControlPlaneEnabled } = useControlPlane();
  const { tenant, billing } = useTenantDetails();
  const { organizations, isUserUniverseLoaded } = useOrganizations();
  const { entitlements } = useOrganizationEntitlements(organizationId);
  const plansQuery = useQuery({
    ...queries.controlPlane.subscriptionPlans(),
    enabled: isControlPlaneEnabled && canBill,
  });
  const paygPlan = payAsYouGoPlan(plansQuery.data?.plans);
  const upgrade = useSubscriptionUpgrade(organizationId);

  const highlightId: ResourceId | null =
    gate === 'tenants'
      ? 'tenants'
      : gate === 'users'
        ? 'users'
        : gate === 'retention'
          ? 'data_retention_days'
          : toResourceId(featureId);

  const comparison = buildComparison({
    planList: plansQuery.data,
    entitlements,
    retentionPeriod,
    highlightId,
  });
  const highlighted = comparison.rows.find((row) => row.id === highlightId);
  const planCode = currentPlanCode(billing?.state);
  const retentionLabel = retentionPeriod
    ? formatRetentionPeriod(retentionPeriod)
    : 'your current window';
  // Free and developer can still move to pay-as-you-go. Anyone already on a
  // paid plan has no longer retention to unlock here, so offer a custom plan.
  const customPlan =
    gate === 'retention' &&
    !!planCode &&
    !canSelfServePayAsYouGoUpgrade(planCode);
  const mode: GateMode =
    gate === 'retention' && billing?.isLoading && !billing.state
      ? 'loading'
      : customPlan
        ? 'custom'
        : 'upgrade';
  const planName = planCode
    ? currentPlanDisplayName(billing?.state?.plans, planCode)
    : COPY.planName;
  // Ownership comes from the loaded org list. Until that list is in, keep
  // Upgrade disabled so it does not flash as clickable for a member.
  const ownerKnown = isUserUniverseLoaded && isControlPlaneEnabled;
  const isOrganizationOwner =
    ownerKnown &&
    organizations.some(
      (org) => org.metadata.id === organizationId && org.isOwner,
    );
  const billingReady = isControlPlaneEnabled && canBill && !!paygPlan;
  // A missing plan (still loading, or a member who cannot read billing) stays
  // on the upgrade action. A known non-free plan has nothing to self-serve
  // here, so the button sends them to billing instead.
  const planPending =
    isControlPlaneEnabled && canBill && !!billing?.isLoading && !planCode;
  const linkToBilling = !!planCode && planCodeBase(planCode) !== 'free';

  return {
    header:
      mode === 'loading'
        ? {
            title: COPY.header.retentionCustom.title,
            description: COPY.header.retentionCustom.checking,
          }
        : mode === 'custom'
          ? {
              title: COPY.header.retentionCustom.title,
              description: COPY.header.retentionCustom.description(
                planName,
                retentionLabel,
              ),
            }
          : buildHeader(gate, highlighted, organizationName),
    comparison,
    currentPlan:
      mode === 'custom' ? { name: planName, retention: retentionLabel } : null,
    mode,
    upgrade,
    canUpgrade:
      billingReady && isOrganizationOwner && !linkToBilling && !planPending,
    ownerOnlyUpgrade:
      billingReady &&
      ownerKnown &&
      !isOrganizationOwner &&
      !linkToBilling &&
      !planPending,
    linkToBilling,
    organizationId,
    onUpgrade: () =>
      paygPlan &&
      upgrade.mutate(paygPlan.planCode, {
        onSuccess: (data) => {
          if (!data.checkoutUrl) {
            onUpgraded?.();
          }
        },
      }),
    salesHref: salesUrl(tenant?.name, tenant?.metadata?.id),
  };
}

type UpgradeGateState = ReturnType<typeof useUpgradeGate>;

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

function ValueCell({
  value,
  note,
  className,
}: {
  value: string;
  note?: string;
  className?: string;
}) {
  return (
    <TableCell className={cn('px-3 py-2 text-right align-top', className)}>
      <span className="block text-sm tabular-nums">{value}</span>
      {note ? (
        <span className="block text-xs font-normal text-muted-foreground">
          {note}
        </span>
      ) : null}
    </TableCell>
  );
}

function ResourceRow({
  row,
  highlighted,
}: {
  row: ComparisonRow;
  highlighted: boolean;
}) {
  return (
    <TableRow className={cn(highlighted && 'bg-primary/5 hover:bg-primary/5')}>
      <TableCell
        className={cn(
          'px-3 py-2 align-top text-sm',
          highlighted ? 'font-medium text-foreground' : 'text-muted-foreground',
        )}
      >
        {row.label}
      </TableCell>
      <ValueCell
        value={row.free}
        note={row.freeNote}
        className="text-muted-foreground"
      />
      <ValueCell
        value={row.payg}
        note={row.paygNote}
        className="font-medium text-foreground"
      />
    </TableRow>
  );
}

function ComparisonSection({
  label,
  note,
  rows,
  highlightId,
}: {
  label: string;
  note?: string;
  rows: ComparisonRow[];
  highlightId: ResourceId | null;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-col gap-0.5">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
      </div>
      <div className="overflow-hidden rounded-lg border border-border/50">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-9 px-3 text-xs">
                {COPY.compare.resource}
              </TableHead>
              <TableHead className="h-9 px-3 text-right text-xs">
                {COPY.freeName}
              </TableHead>
              <TableHead className="h-9 px-3 text-right text-xs text-foreground">
                {COPY.planName}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <ResourceRow
                key={row.id}
                row={row}
                highlighted={row.id === highlightId}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const SECTIONS: { kind: ResourceKind; label: string; note?: string }[] = [
  {
    kind: 'metered',
    label: COPY.compare.groups.metered,
    note: COPY.compare.groups.meteredNote,
  },
  { kind: 'limit', label: COPY.compare.groups.limits },
];

function ComparisonTable({ comparison }: { comparison: Comparison }) {
  const highlighted = comparison.rows.find(
    (row) => row.id === comparison.highlightId,
  );
  // With no gated resource there is nothing to collapse to, so show it all.
  const [expanded, setExpanded] = useState(!highlighted);

  // Collapsed: only the gated row, under its own section label so a metered
  // resource still reads as included-then-overage.
  const sections = expanded
    ? SECTIONS.map((section) => ({
        ...section,
        rows: comparison.rows.filter((row) => row.kind === section.kind),
      }))
    : highlighted
      ? SECTIONS.filter((section) => section.kind === highlighted.kind).map(
          (section) => ({ ...section, rows: [highlighted] }),
        )
      : [];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-4">
        {sections.map((section) => (
          <ComparisonSection
            key={section.kind}
            label={section.label}
            note={section.note}
            rows={section.rows}
            highlightId={comparison.highlightId}
          />
        ))}
      </div>
      {highlighted ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-fit gap-1 px-2 text-xs text-muted-foreground"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? COPY.compare.collapse : COPY.compare.expand}
          <ChevronDownIcon
            className={cn(
              'size-3 transition-transform',
              expanded && 'rotate-180',
            )}
          />
        </Button>
      ) : null}
    </div>
  );
}

function CurrentPlanRetention({
  planName,
  retention,
}: {
  planName: string;
  retention: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {COPY.compare.currentPlan}
      </p>
      <div className="overflow-hidden rounded-lg border border-border/50">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-9 px-3 text-xs">
                {COPY.compare.resource}
              </TableHead>
              <TableHead className="h-9 px-3 text-right text-xs text-foreground">
                {planName}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="bg-primary/5 hover:bg-primary/5">
              <TableCell className="px-3 py-2 align-top text-sm font-medium text-foreground">
                Data retention
              </TableCell>
              <TableCell className="px-3 py-2 text-right align-top text-sm font-medium tabular-nums text-foreground">
                {retention}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function UpgradeGateBody({ state }: { state: UpgradeGateState }) {
  const { comparison, upgrade, salesHref, mode, currentPlan } = state;

  if (mode === 'loading') {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  if (mode === 'custom' && currentPlan) {
    return (
      <CurrentPlanRetention
        planName={currentPlan.name}
        retention={currentPlan.retention}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ComparisonTable comparison={comparison} />

      {upgrade.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{COPY.error.title}</AlertTitle>
          <AlertDescription>
            {getPlanChangeErrorMessage(upgrade.error)}
          </AlertDescription>
        </Alert>
      ) : null}

      <p className="text-xs leading-5 text-muted-foreground">
        {COPY.actions.footnote}{' '}
        <a
          href={PRICING_URL}
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-4 hover:text-foreground"
        >
          {COPY.actions.pricing}
        </a>{' '}
        {COPY.actions.salesLead}{' '}
        <a
          href={salesHref}
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-4 hover:text-foreground"
        >
          {COPY.actions.sales}
        </a>
      </p>
    </div>
  );
}

function UpgradeButton({
  disabled,
  pending,
  ownerOnly,
  onUpgrade,
}: {
  disabled: boolean;
  pending: boolean;
  ownerOnly: boolean;
  onUpgrade: () => void;
}) {
  const button = (
    <Button type="button" size="sm" disabled={disabled} onClick={onUpgrade}>
      {pending ? <Spinner /> : COPY.actions.upgrade}
    </Button>
  );

  // A disabled button does not receive hover, so the tooltip sits on a span.
  if (!ownerOnly) {
    return button;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent>{COPY.actions.ownerOnly}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function UpgradeGateFooter({
  state,
  onDismiss,
}: {
  state: UpgradeGateState;
  onDismiss?: () => void;
}) {
  const {
    upgrade,
    canUpgrade,
    ownerOnlyUpgrade,
    linkToBilling,
    organizationId,
    onUpgrade,
    mode,
    salesHref,
  } = state;

  if (mode === 'loading') {
    return onDismiss ? (
      <Button type="button" variant="outline" size="sm" onClick={onDismiss}>
        {COPY.actions.dismiss}
      </Button>
    ) : null;
  }

  if (mode === 'custom') {
    return (
      <>
        {onDismiss ? (
          <Button type="button" variant="outline" size="sm" onClick={onDismiss}>
            {COPY.actions.dismiss}
          </Button>
        ) : null}
        <Button type="button" size="sm" asChild>
          <a href={salesHref} target="_blank" rel="noreferrer">
            {COPY.actions.contact}
          </a>
        </Button>
      </>
    );
  }

  return (
    <>
      {onDismiss ? (
        <Button type="button" variant="outline" size="sm" onClick={onDismiss}>
          {COPY.actions.dismiss}
        </Button>
      ) : null}
      {linkToBilling ? (
        <Button type="button" size="sm" asChild>
          <Link
            to={appRoutes.organizationBillingRoute.to}
            params={{ organization: organizationId }}
            onClick={onDismiss}
          >
            {COPY.actions.billing}
          </Link>
        </Button>
      ) : (
        <UpgradeButton
          disabled={!canUpgrade || upgrade.isPending}
          pending={upgrade.isPending}
          ownerOnly={ownerOnlyUpgrade}
          onUpgrade={onUpgrade}
        />
      )}
    </>
  );
}

/**
 * Inline (non-dialog) variant, used where the gate replaces a full-screen
 * form such as tenant creation.
 */
export function UpgradeGateContent({ onDismiss, ...props }: UpgradeGateProps) {
  const state = useUpgradeGate(props);

  return (
    <SetupCard
      className="max-w-none"
      title={state.header.title}
      description={state.header.description}
      footer={<UpgradeGateFooter state={state} onDismiss={onDismiss} />}
    >
      <UpgradeGateBody state={state} />
    </SetupCard>
  );
}

export function UpgradeGateDialog({
  open,
  onDismiss,
  ...props
}: UpgradeGateProps & { open: boolean; onDismiss: () => void }) {
  const state = useUpgradeGate({ ...props, onUpgraded: onDismiss });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onDismiss()}>
      <DialogContent
        className={`${setupCardDialogClassName} max-h-[85vh] max-w-xl overflow-y-auto`}
      >
        <DialogTitle className="sr-only">{state.header.title}</DialogTitle>
        <SetupCard
          className="max-w-none"
          title={state.header.title}
          description={
            <DialogDescription>{state.header.description}</DialogDescription>
          }
          footer={<UpgradeGateFooter state={state} onDismiss={onDismiss} />}
        >
          <UpgradeGateBody state={state} />
        </SetupCard>
      </DialogContent>
    </Dialog>
  );
}
